import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import type {
  QuoteItemDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
// eslint-disable-next-line import/no-extraneous-dependencies
import { generateKeyBetween } from 'fractional-indexing';
import { addQuoteV2FreehandMutation } from 'Services/Mutations/QuotesV2';
import type { AddQuoteV2FreehandResponse } from 'Services/Query/QuotesV2/Types';
import {
  getLastPositionInContainer,
  calculateGrossProfitPercentage
} from '../utils';
import { updateItemInTree } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

export interface AddFreehandVariables {
  name: string;
  sku: string;
  sell: number;
  cost: number;
  vendorName: string;
  sectionId?: string;
  note?: string;
  unitOfMeasure?: string;
  packSize?: string;
}

const buildOptimisticItem = (
  vars: AddFreehandVariables,
  tempId: string
): QuoteProductDTO => {
  const {
    name,
    sku,
    sell,
    cost,
    vendorName,
    sectionId,
    note,
    unitOfMeasure,
    packSize
  } = vars;

  return {
    _id: tempId,
    id: tempId,
    type: 'product',
    productId: '',
    name,
    sku,
    quantity: 1,
    sell,
    cost,
    note: note || null,
    vendor: { name: vendorName, _id: null, vendorId: null },
    images: null,
    imageUrl: '',
    isFreeHand: true,
    isEditable: true,
    labelType: '',
    customLabel: '',
    includeInTotalCost: false,
    minimumQuoteQuantity: 1,
    overrideMinimumQuoteQuantity: true,
    children: [],
    parentID: sectionId || null,
    totalGrossProfit: sell - cost,
    totalGrossProfitPercentage: calculateGrossProfitPercentage(cost, sell),
    description: '',
    packSize: packSize || '',
    unitOfMeasure: unitOfMeasure || '',
    manufacturerSku: '',
    isPricingFetching: true
  } as unknown as QuoteProductDTO;
};

const addItemOptimistically = (
  items: QuoteItemDTO[],
  newItem: QuoteProductDTO,
  sectionId: string
): QuoteItemDTO[] => {
  if (!sectionId) {
    return [...items, newItem];
  }

  return items.map(item => {
    if (
      item.type === 'section' &&
      (item.id || (item as any)._id) === sectionId
    ) {
      return {
        ...item,
        children: [...((item as any).children || []), newItem]
      };
    }
    return item;
  });
};

/**
 * Add a freehand (SPO) item to a quote via the dedicated v2 freehand endpoint.
 * Applies an optimistic update immediately, reverts on error, and invalidates
 * on success to sync the real server data.
 */
export const useAddFreehand = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = addQuoteV2FreehandMutation();

  return useMutation({
    mutationFn: ({
      name,
      sku,
      sell,
      cost,
      vendorName,
      sectionId = '',
      note = '',
      unitOfMeasure = '',
      packSize = ''
    }: AddFreehandVariables) => {
      const items = cache.getItems() || [];
      const lastPos = getLastPositionInContainer(items, sectionId || null);
      const position = generateKeyBetween(lastPos, null);

      return mutationFn({
        quoteId,
        name,
        sku,
        sell,
        cost,
        quantity: 1,
        position,
        note,
        sectionId,
        vendorName,
        unitOfMeasure,
        packSize
      });
    },

    onMutate: async (variables: AddFreehandVariables) => {
      await cache.cancel();

      const previousItems = cache.getItems();
      const tempId = uuidv4();

      // Compute position from pre-optimistic data, scoped to target container
      const lastPos = getLastPositionInContainer(
        previousItems || [],
        variables.sectionId || null
      );
      const position = generateKeyBetween(lastPos, null);

      const newItem = {
        ...buildOptimisticItem(variables, tempId),
        position
      } as unknown as QuoteProductDTO;

      cache.setItems(old =>
        addItemOptimistically(old || [], newItem, variables.sectionId || '')
      );

      return { previousItems, tempId };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data, _variables, context) => {
      const response = data as AddQuoteV2FreehandResponse;
      const serverItem = response?.item;
      if (serverItem?._id && context?.tempId) {
        cache.setItems(old =>
          old
            ? updateItemInTree(old, context.tempId, {
                _id: serverItem._id,
                id: serverItem._id,
                isPricingFetching: false
              })
            : old
        );
      }
      if (response?.totals) {
        cache.setQuote(old =>
          old
            ? {
                ...old,
                totalSell: response.totals.totalSell,
                totalCost: response.totals.totalCost
              }
            : old
        );
      }
      cache.invalidate();
    }
  });
};
